import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Chip, IconButton, InputAdornment, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';

import useNotification from '../../../../../../core/hooks/use_notification';
import { getAllWarehousesAction, getAllWarehousesState } from '../../../../../../features/inventory/get_all_warehouses';
import { getWarehouseStockAction } from '../../../../../../features/inventory/get_warehouse_stock';
import { getAllWarehousesNotificationData, getWarehouseStockNotificationData } from '../../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../../core/enums/common/api';
import { WarehouseTypeEnum } from '../../../../../../core/enums/inventory/warehouse';
import { ExpirationChip, formatNumber } from '../../../stocks/stock_utils';

// order destinations know the pod / pop, the stock lives in the inventory warehouse linked to it
const findDestinationWarehouse = (warehouses, destination) => {
    if (destination?.destinationPodId) {
        return warehouses?.find(warehouse => warehouse?.type === WarehouseTypeEnum.POD.value && warehouse?.pod?.podId === destination.destinationPodId);
    }

    if (destination?.destinationPopId) {
        return warehouses?.find(warehouse => warehouse?.type === WarehouseTypeEnum.POP.value && warehouse?.pop?.popId === destination.destinationPopId);
    }

    if (destination?.warehouseId) {
        return warehouses?.find(warehouse => warehouse?.internalId === destination.warehouseId);
    }

    return warehouses?.find(warehouse => warehouse?.type === WarehouseTypeEnum.MAIN.value);
};

/**
 * Current stock of an order destination shown next to its product list, so the order can be completed while looking at it.
 * Products already in the order but missing in stock are listed with zero, the lowest stock comes first.
 */
const DestinationStockPanel = ({ destination, orderedProducts, onClose }) => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();
    const getAllWarehouses = useSelector(getAllWarehousesState);

    const [stock, setStock] = useState(null);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');

    useEffect(() => {
        if (!getAllWarehouses?.data && !getAllWarehouses?.loading) {
            dispatch(getAllWarehousesAction())
                .then(response => showErrorMessage(getAllWarehousesNotificationData(response)));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const warehouse = useMemo(() => findDestinationWarehouse(getAllWarehouses?.data, destination), [getAllWarehouses?.data, destination]);

    // the result is kept locally, several destinations can show their stock at the same time
    const loadStock = useCallback(() => {
        if (!warehouse) return;

        setLoading(true);
        dispatch(getWarehouseStockAction(warehouse.internalId))
            .then(response => {
                showErrorMessage(getWarehouseStockNotificationData(response));
                setStock(response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED ? response.payload : null);
                setLoading(false);
            });
    }, [dispatch, warehouse, showErrorMessage]);

    useEffect(() => {
        loadStock();
    }, [loadStock]);

    const rows = useMemo(() => {
        const byProduct = new Map();

        (stock?.products || []).forEach(product => byProduct.set(product.productId, {
            productId: product.productId,
            name: product.productName,
            quantity: product.quantity,
            expiredQuantity: product.batches.filter(batch => batch.expired).reduce((sum, batch) => sum + batch.quantity, 0),
            nearestExpirationDate: product.nearestExpirationDate,
            ordered: 0
        }));

        (orderedProducts || []).filter(product => product?.productId).forEach(product => {
            const row = byProduct.get(product.productId) || {
                productId: product.productId,
                name: product.name,
                quantity: 0,
                expiredQuantity: 0,
                nearestExpirationDate: null,
                ordered: 0
            };
            row.ordered += Number(product.quantity) || 0;
            byProduct.set(product.productId, row);
        });

        return [...byProduct.values()]
            .filter(row => (row.name || '').toLowerCase().includes(query.trim().toLowerCase()))
            .sort((a, b) => (a.quantity - a.expiredQuantity) - (b.quantity - b.expiredQuantity) || (a.name || '').localeCompare(b.name || ''));
    }, [stock, orderedProducts, query]);

    const warehousesLoaded = !!getAllWarehouses?.data;

    return (
        <Paper variant="outlined" sx={ { p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 } }>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={ 1 }>
                <Box>
                    <Typography variant="subtitle1" sx={ { fontWeight: 700 } }>Stan magazynowy</Typography>
                    <Typography variant="caption" color="text.secondary">
                        { stock ? `${ formatNumber(stock.products.length) } produktów · ${ formatNumber(stock.quantity) } szt.` : destination?.name }
                    </Typography>
                </Box>
                <Stack direction="row">
                    <Tooltip title="Odśwież">
                        <span>
                            <IconButton size="small" onClick={ loadStock } disabled={ !warehouse || loading }>
                                <RefreshIcon fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Zamknij">
                        <IconButton size="small" onClick={ onClose }>
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            <TextField
                size="small"
                fullWidth
                placeholder="Szukaj produktu"
                value={ query }
                onChange={ event => setQuery(event.target.value) }
                InputProps={ { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action"/></InputAdornment> } }
            />

            <Box sx={ { height: 4 } }>
                { (loading || getAllWarehouses?.loading) && <LinearProgress/> }
            </Box>

            {
                warehousesLoaded && !warehouse
                    ? (
                        <Typography variant="body2" color="text.secondary" sx={ { py: 2, textAlign: 'center' } }>
                            Ta lokalizacja nie jest powiązana z magazynem w systemie magazynowym
                        </Typography>
                    )
                    : (
                        <TableContainer sx={ { flexGrow: 1, maxHeight: 520 } }>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow sx={ { '& th': { whiteSpace: 'nowrap' } } }>
                                        <TableCell>Produkt</TableCell>
                                        <TableCell align="right">Stan</TableCell>
                                        <TableCell align="right">Zamówiono</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        rows.map(row => (
                                            <TableRow key={ row.productId } sx={ { verticalAlign: 'top' } }>
                                                <TableCell>
                                                    <Typography variant="body2" sx={ { fontWeight: 500 } }>{ row.name || `Produkt #${ row.productId }` }</Typography>
                                                    { row.nearestExpirationDate && (
                                                        <Box sx={ { mt: 0.5 } }>
                                                            <ExpirationChip date={ row.nearestExpirationDate }/>
                                                        </Box>
                                                    ) }
                                                </TableCell>
                                                <TableCell align="right">
                                                    {
                                                        row.quantity === 0
                                                            ? <Chip size="small" variant="outlined" label="brak"/>
                                                            : <Typography variant="body2" sx={ { fontWeight: 600 } }>{ formatNumber(row.quantity) }</Typography>
                                                    }
                                                    { row.expiredQuantity > 0 && (
                                                        <Tooltip title="Sztuki przeterminowane, wliczone w stan">
                                                            <Typography variant="caption" color="error.main" sx={ { display: 'block', whiteSpace: 'nowrap' } }>
                                                                { formatNumber(row.expiredQuantity) } przeterm.
                                                            </Typography>
                                                        </Tooltip>
                                                    ) }
                                                </TableCell>
                                                <TableCell align="right">
                                                    { row.ordered > 0 ? formatNumber(row.ordered) : '-' }
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    }
                                    {
                                        !loading && stock && rows.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={ 3 } align="center" sx={ { py: 3, color: 'text.secondary' } }>
                                                    { query ? 'Brak produktów spełniających kryteria' : 'Magazyn jest pusty' }
                                                </TableCell>
                                            </TableRow>
                                        )
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )
            }
        </Paper>
    );
};

export default DestinationStockPanel;
